export type BlogDocument = {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  authorName: string;
  publishedAt: string;
  readTimeMinutes: number;
  seoTitle: string;
  seoDescription: string;
  coverImageUrl?: string;
  heroHtml: string;
  bodyHtml: string;
  status?: "draft" | "published" | "archived";
};

export type BlogRecord = {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author_name: string;
  published_at: string;
  read_time_minutes: number;
  seo_title: string;
  seo_description: string;
  cover_image_url?: string | null;
  hero_html: string;
  body_html: string;
  author_user_id?: string | null;
  status?: string;
};

const STORAGE_KEY = "choice-wealth-generated-blogs";

export const sampleBlogs: BlogDocument[] = [
  // Static sample blog commented out.
  // {
  //   slug: "portfolio-context-pms-evaluation",
  //   title: "Evaluate PMS In Portfolio Context, Not In Isolation",
  //   excerpt:
  //     "A blog template example showing how generated HTML content can plug into the animated article route while keeping the premium editorial motion already used across the site.",
  //   category: "Portfolio Review",
  //   authorName: "Choice Wealth Research",
  //   publishedAt: "2026-05-24",
  //   readTimeMinutes: 6,
  //   seoTitle: "Evaluate PMS In Portfolio Context | Choice Wealth",
  //   seoDescription:
  //     "A sample generated blog article route for PMS analysis, rendered from HTML fields and enhanced with the site’s existing motion system.",
  //   heroHtml: "...",
  //   bodyHtml: "...",
  // },
];

export function getStoredBlogs() {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }

    return JSON.parse(raw) as BlogDocument[];
  } catch {
    return [];
  }
}

export function saveBlog(blog: BlogDocument) {
  if (typeof window === "undefined") {
    return;
  }

  const next = [blog, ...getStoredBlogs().filter((entry) => entry.slug !== blog.slug)];
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

export function deleteStoredBlog(slug: string) {
  if (typeof window === "undefined") {
    return;
  }

  const next = getStoredBlogs().filter((entry) => entry.slug !== slug);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

export function getAllBlogs() {
  const stored = getStoredBlogs();
  const deduped = new Map<string, BlogDocument>();

  [...stored, ...sampleBlogs].forEach((blog) => {
    if (!deduped.has(blog.slug)) {
      deduped.set(blog.slug, blog);
    }
  });

  return [...deduped.values()];
}

export function getPublishedBlogs() {
  return getAllBlogs().filter((blog) => (blog.status ?? "published") === "published");
}

export function getBlogBySlug(slug: string) {
  return getAllBlogs().find((blog) => blog.slug === slug) ?? null;
}

export function getPublishedBlogBySlug(slug: string) {
  return getPublishedBlogs().find((blog) => blog.slug === slug) ?? null;
}

export function toBlogDocument(record: BlogRecord): BlogDocument {
  return {
    slug: record.slug,
    title: record.title,
    excerpt: record.excerpt,
    category: record.category,
    authorName: record.author_name,
    publishedAt: record.published_at,
    readTimeMinutes: record.read_time_minutes,
    seoTitle: record.seo_title,
    seoDescription: record.seo_description,
    coverImageUrl: record.cover_image_url ?? undefined,
    heroHtml: record.hero_html,
    bodyHtml: record.body_html,
    status: (record.status as BlogDocument["status"]) ?? "draft",
  };
}

export function toBlogRecord(blog: BlogDocument, status = "published"): BlogRecord {
  return {
    slug: blog.slug,
    title: blog.title,
    excerpt: blog.excerpt,
    category: blog.category,
    author_name: blog.authorName,
    published_at: blog.publishedAt,
    read_time_minutes: blog.readTimeMinutes,
    seo_title: blog.seoTitle,
    seo_description: blog.seoDescription,
    cover_image_url: blog.coverImageUrl ?? null,
    hero_html: blog.heroHtml,
    body_html: blog.bodyHtml,
    status: blog.status ?? status,
  };
}
